---
title: "Using Charting Tools and Indicators on {{BRAND}}"
slug: charting-tools-indicators
shortTitle: "Charting Tools & Indicators"
description: "The {{BRAND}} trade chart explained: timeframes, chart types, settings and order lines, where to find RSI and moving-average readings, and how to pair an external chart."
level: intermediate
category: trading
course: platform-guide
order: 6
topics: [technical-analysis, indicators]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/charting-tools-indicators.svg
faqs:
  - q: "Can I add indicators like RSI or moving averages to the trade chart?"
    a: "Not to the trade chart itself, which has no indicators or drawing tools. RSI and moving-average-cross readings appear in the opportunity scanner and quick view on the Markets page."
  - q: "Can I draw trendlines on the {{BRAND}} chart?"
    a: "There are no drawing tools on the trade chart. Many traders draw their levels in a separate charting tool, then place and manage their orders on {{BRAND}}."
  - q: "Why does my external chart show slightly different prices?"
    a: "Data sources differ. {{BRAND}} quotes a Bid and an Ask around its own reference price, while another chart may show one exchange's prices or a different feed. Your orders, TP and SL always trigger on {{BRAND}}'s prices."
  - q: "What is a log scale and when is it useful?"
    a: "A log scale spaces prices by percentage change rather than by dollar amount, so a move from 20,000 to 40,000 takes the same height as a move from 40,000 to 80,000. It helps on long time ranges and in markets that have moved a long way."
  - q: "Can I save my chart setup?"
    a: "Yes. The chart settings let you save up to three templates with your preferred lines, colors and display options."
---

The trade chart on {{BRAND}} is built for two jobs: reading price and managing your orders. It gives you clean candles, six timeframes, several chart types and a settings panel, and it draws your entry, take profit and stop loss as lines you can drag on desktop. What it does not include are indicators or drawing tools.

This guide shows how to set the chart up, how to trade from it, where RSI and moving-average readings appear elsewhere on the platform, and how to work alongside a separate charting tool if you want to draw trendlines or add indicators of your own.

<CTA />

<KeyTakeaways>

- **Six timeframes, six chart types.** Choose 1m, 5m, 15m, 1h, 4h or 1D, and candles, hollow candles, Heikin-Ashi, bars, line or area.
- **The gear icon controls the look.** Order lines, colors, the volume pane, log or % scale, time zone and three saved templates.
- **Manage orders from the chart.** On desktop you can drag pending orders and TP / SL lines to new prices, with a confirmation step.
- **Indicators live elsewhere.** RSI and moving-average readings are on the Markets page; for drawing, use an external tool, remembering your orders trigger on {{BRAND}}'s prices.

</KeyTakeaways>

## Timeframes and chart types

A **timeframe** is the period each candle or bar covers. On the 1h chart, each candle is one hour of trading; on the 1D chart, one day. Shorter timeframes show more detail and more noise; longer ones show the bigger picture. Many traders check a longer timeframe for the overall direction, then a shorter one for timing; [Multi-Timeframe Analysis](/academy/article/multi-timeframe-analysis) explains the method.

<CompareTable caption="The six chart types">

| Chart type | What it shows | Worth knowing |
| --- | --- | --- |
| Candles | Open, high, low and close for each period | The standard view; most lessons assume it |
| Hollow candles | The same data, with hollow and filled bodies | An extra visual cue for the direction of each period |
| Heikin-Ashi | Averaged candles that smooth out noise | Prices are averaged, so don't read entries or stops from them |
| Bars | Open, high, low and close as a bar with ticks | The same data as candles in a slimmer form |
| Line | Closing prices joined by a line | Clean for spotting the overall direction |
| Area | A line chart with the space below shaded | Useful for a quick overview |

</CompareTable>

If candles are new to you, [Reading a Candlestick Chart](/academy/article/reading-candlestick-charts) explains how to read one.

## Setting up the chart

<Steps>
<Step title="Open the settings">

Click the gear icon on the chart.

</Step>
<Step title="Choose your lines">

Set the style for the entry, TP, SL, pending-order, break-even, bid / ask and last-price lines, and switch on **P&L on position lines** to see each position's live result on the chart.

</Step>
<Step title="Adjust the display">

Pick colors, the grid, the volume pane, the OHLC legend (the open, high, low and close of the candle under your cursor), axis tags and a candle countdown showing the time left in the current candle.

</Step>
<Step title="Set scale and time">

Choose a normal, log or % scale, and UTC or local time.

</Step>
<Step title="Save a template">

Save the setup as one of three templates, for example one for fast intraday charts and one for daily analysis.

</Step>
</Steps>

<Screenshot name="chart-settings-panel" alt="The chart settings panel opened from the gear icon, with line styles, display options, scale and templates" caption="The gear icon opens the chart settings: lines, display, scale, time zone and templates." />

{/* TODO: verify with platform — what the volume pane measures for CFD markets, and the reference point of the % scale. */}

Showing the bid and ask lines is especially useful. Buys fill at the Ask, while a buy's TP and SL trigger on the Bid, so seeing both quotes tells you how close a level really is.

<Example title="Why a log scale changes the picture (illustrative prices)">

Bitcoin moves from 20,000 to 40,000, then from 40,000 to 60,000. Each move is 20,000 dollars, so on a normal scale they look the same height. In percentage terms they are very different: 20,000 ÷ 20,000 = **+100%**, then 20,000 ÷ 40,000 = **+50%**. On a log scale, the first move is drawn about 1.7 times taller, which matches how much it changed the value of a position.

</Example>

If you scroll back through history, a **Back to latest** pill returns you to the current candle, and **Reset view** restores the default zoom.

## Managing orders on the chart

When you hold a position, the chart draws its entry, TP and SL as lines, and any pending orders as their own lines. On desktop you can drag a pending order or a TP / SL line to a new price. The platform asks you to confirm before anything changes.

<Screenshot name="chart-order-lines" alt="The trade chart with entry, take profit, stop loss and pending order lines" caption="Entry, TP, SL and pending order lines; drag one on desktop and confirm the new price." />

<ProTip>

Turn on **P&L on position lines** and the break-even line. Before dragging a stop, you can then see at a glance whether the new level would lock in a gain or still risk a loss.

</ProTip>

## Where to find indicator readings

The trade chart has no indicators or drawing tools. Instead, the **Markets** page's opportunity scanner and quick view show **RSI** and **moving-average-cross** readings for each market.

{/* TODO: verify with platform — confirm the trade chart has no indicators or drawing tools, and which timeframe and settings the scanner uses for RSI and moving-average crosses. */}

- **RSI** (Relative Strength Index) is a momentum reading from 0 to 100. Readings above 70 are often called overbought and below 30 oversold. These are conventions, not signals: in a strong trend RSI can stay high or low for a long time.
- A **moving-average cross** happens when a shorter-term average of price crosses a longer-term one. It describes a change in trend after the fact, so it tends to lag.

<Screenshot name="markets-scanner-quick-view" alt="The Markets page opportunity scanner and quick view showing RSI and moving-average-cross readings" caption="RSI and moving-average-cross readings appear in the Markets scanner and quick view." />

To learn how these readings work, see [RSI, MACD and Stochastic](/academy/article/rsi-macd-stochastic) and [Moving Averages](/academy/article/moving-averages).

## Other analysis tools on the Markets page

The Markets page carries more context that the trade chart does not show:

- **Economic calendar and news.** Scheduled events such as interest-rate decisions or inflation data often bring sharp, fast moves, so check them before you place or move a stop.
- **AI market brief.** A short written summary for a market, including a "why is this moving?" explanation when the price moves sharply. Treat it as a starting point for your own research, not as a signal.
- **Sentiment.** The share of {{BRAND}} clients who are long a market. It shows how other clients are positioned, not where the price will go.

{/* TODO: verify with platform — exact names and placement of the AI market brief and "why is this moving?" features. */}

## Pairing an external charting tool

If you want trendlines, Fibonacci levels or indicators on the chart itself, you can do your analysis in a separate charting tool and place your orders on {{BRAND}}. You don't need to give that tool your {{BRAND}} login.

<Steps>
<Step title="Analyze in your charting tool">

Draw your levels and add your indicators as usual.

</Step>
<Step title="Compare the prices">

Put the two charts side by side and note the gap between the external price and {{BRAND}}'s Bid and Ask at the same moment.

</Step>
<Step title="Translate your levels">

Adjust each level for that gap before you use it on {{BRAND}}.

</Step>
<Step title="Place orders and alerts on {{BRAND}}">

Enter Limit / Stop orders and TP / SL on {{BRAND}}, and set price alerts from **Account → Alerts** or the bell on a market row, so everything runs on the prices your orders actually use.

</Step>
</Steps>

<Example title="Translating a stop level (illustrative prices)">

Your external chart shows Bitcoin at 60,040, while {{BRAND}} shows Bid 60,000 and Ask 60,030. Your analysis puts a buy's stop at 57,700, just under a swing low. The external feed is running about 60,040 − 60,000 = 40 above the {{BRAND}} Bid, so the equivalent level is about 57,700 − 40 = **57,660**. The gap moves over time, so check it again before you adjust.

</Example>

<Warning>

Your orders, TP and SL trigger on {{BRAND}}'s prices, not on another chart's. A level touched on one chart may not be touched on the other, and the reverse. Always confirm a level against the {{BRAND}} chart before relying on it.

</Warning>

## Conclusion

The {{BRAND}} trade chart gives you six timeframes, six chart types, a detailed settings panel with three templates, and order lines you can drag on desktop. It has no indicators or drawing tools: RSI and moving-average readings sit in the Markets scanner and quick view, and an external charting tool can fill the gap, as long as you translate its levels to {{BRAND}}'s prices. Indicators describe past price; they do not predict it, so treat them as context, not certainty.
